import { useEffect, useRef, useState } from 'react';

/**
 * Allows for different behavior on close depending on where mouseevent happened.
 *
 * Exposes a delayed trigger for showing/hiding text on openChange.
 */
export const useOpenChangeHandlers = ({
	isOpen,
	setIsOpen,
}: {
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
	const [showTriggerText, setShowTriggerText] = useState(isOpen);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Ensure consistent state on mount
	useEffect(() => {
		setShowTriggerText(!isOpen);
	}, [isOpen]);

	const handleImmediateOpenChange = () => {
		// console.log('immediate open change');
		setIsOpen((prev) => !prev);
		setShowTriggerText((prev) => !prev);
	};

	const handleDelayedOpenChange = (e?: Event) => {
		// console.log('delayed open change', e);
		e?.preventDefault();

		// Clear any existing timeout to prevent race conditions
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}

		// @ts-expect-error
		if (e?.detail?.originalEvent?.clientX < 58) {
			setShowTriggerText(isOpen);
		} else {
			// When opening (currently closed)
			if (!isOpen) {
				setShowTriggerText(false); // Immediately hide text when opening
			}
			// When closing (currently open)
			else {
				timeoutRef.current = setTimeout(() => {
					setShowTriggerText(true);
					timeoutRef.current = null;
				}, 220);
			}
		}

		setIsOpen(!isOpen);
	};

	// Synchronize showTriggerText with isOpen changes from outside the hook
	useEffect(() => {
		// This ensures text width is properly set when isOpen changes from external sources
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}

		setShowTriggerText(!isOpen);
	}, [isOpen]);

	// Clean up timeout on unmount
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	return {
		handleDelayedOpenChange,
		handleImmediateOpenChange,
		showTriggerText,
	};
};
